import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  DateRangePicker,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { FacilityPicker } from "components/FacilityPicker";
import { MultipleFacilityPicker } from "components/MultipleFacilityPicker";
import { StateOfMatterPicker } from "components/StateOfMatterPicker";
import { UnifiedTagsInput } from "components/UnifiedTagsInput";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  FacilityPickerFragment,
  MultipleFacilityPickerFragment,
} from "generated-graphql/graphql";
import { FilterKey, FilterKeyType } from "hooks/useOmnisearchDatagrid";
import { sortBy } from "lodash-es";
import { DateTime } from "luxon";
import { TAGS_QUERY } from "queries/tags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MultipleTenantPicker, TenantOption } from "../MultipleTenantPicker";
import { MultipleUserPicker, UserOption } from "../MultipleUserPicker";
import { TimeFilter } from "./TimeFilterInput";

// Semantic equivalents mapping - defines which keys are interchangeable
const SEMANTIC_EQUIVALENTS: Record<string, string[]> = {
  tags: ["tags", "facilityTags"],
  facilityId: ["facility", "facilityId"],
  tenantId: ["tenant", "tenantId"],
  // Add other equivalents as needed in the future
};

// Helper to get the canonical key for any variant
function getCanonicalKey(key: string): string {
  for (const [canonical, variants] of Object.entries(SEMANTIC_EQUIVALENTS)) {
    if (variants.includes(key)) {
      return canonical;
    }
  }
  return key;
}

// Date picker shortcuts for date range filters
const datePickerShortcuts = [
  {
    label: `${new Date().getFullYear()}`,
    getValue: () => {
      const today = DateTime.now();
      const start = DateTime.fromObject({ year: today.year, month: 1, day: 1 });
      const end = DateTime.fromObject({ year: today.year, month: 12, day: 31 });
      return [start, end];
    },
  },
  {
    label: `${new Date().getFullYear() - 1}`,
    getValue: () => {
      const today = DateTime.now();
      const start = DateTime.fromObject({
        year: today.year - 1,
        month: 1,
        day: 1,
      });
      const end = DateTime.fromObject({
        year: today.year - 1,
        month: 12,
        day: 31,
      });
      return [start, end];
    },
  },
  {
    label: `${new Date().getFullYear() - 2}`,
    getValue: () => {
      const today = DateTime.now();
      const start = DateTime.fromObject({
        year: today.year - 2,
        month: 1,
        day: 1,
      });
      const end = DateTime.fromObject({
        year: today.year - 2,
        month: 12,
        day: 31,
      });
      return [start, end];
    },
  },
  {
    label: "Last Quarter",
    getValue: () => {
      const today = DateTime.now();
      const currentQuarter = Math.floor((today.month - 1) / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const lastQuarterYear =
        currentQuarter === 0 ? today.year - 1 : today.year;
      const start = DateTime.fromObject({
        year: lastQuarterYear,
        month: lastQuarter * 3 + 1,
        day: 1,
      });
      const end = DateTime.fromObject({
        year: lastQuarterYear,
        month: (lastQuarter + 1) * 3,
        day: 1,
      }).endOf("month");
      return [start, end];
    },
  },
  {
    label: "This Quarter",
    getValue: () => {
      const today = DateTime.now();
      const currentQuarter = Math.floor((today.month - 1) / 3);
      const start = DateTime.fromObject({
        year: today.year,
        month: currentQuarter * 3 + 1,
        day: 1,
      });
      const end = DateTime.fromObject({
        year: today.year,
        month: (currentQuarter + 1) * 3,
        day: 1,
      }).endOf("month");
      return [start, end];
    },
  },
  {
    label: "Reset",
    getValue: () => [null, null],
  },
];

// Define the type for filter groups
export type FilterGroup = {
  name: string;
  fields: string[];
};

type FilterSection = {
  title: string;
  fields: FilterField[];
};

type TagType = "facility" | "task" | "chemical" | "workflow" | "waste";
export type FilterField = {
  key: string;
  label: string;
  type:
    | "facility"
    | "wasteFacility"
    | "multipleFacility"
    | "wasteMultipleFacility"
    | "multipleTenant"
    | "multipleUser"
    | "text"
    | "state"
    | "county"
    | "address"
    | "tags"
    | "facilityTags"
    | "taskTags"
    | "boolean"
    | "enum"
    | "enumAutocomplete"
    | "number"
    | "stateOfMatter"
    | "time"
    | "date";
  placeholder?: string;
  enumValues?: string[];
  enumPresentationFunction?: (enumValue: string) => string;
  // For tags fields, specify the tag type for backend queries
  tagType?: TagType;
  // For date range fields, enable/disable shortcuts (defaults to true)
  dateRangeShortcuts?: boolean;
  // For conditional disabling based on other field values
  disableFilterWhen?: {
    field: string;
    value: string | boolean;
  };
  // When set, renders a checkbox below the text input that sets the value to "null"
  nullCheckboxLabel?: string;
};

export type InputValues = {
  [key: string]: any;
};

// Helper function to determine field type from key
function getFieldType(
  key: string,
  filterKeyType?: string
): FilterField["type"] {
  if (filterKeyType) {
    // Treat tag types as tags type to use SimpleTagsInput
    if (
      ["facilityTags", "taskTags", "chemicalTags", "workflowTags", "wasteTags"].includes(
        filterKeyType
      )
    ) {
      return "tags";
    }
    return filterKeyType as FilterField["type"];
  }

  // Default type mappings based on key names
  if (key === "facility" || key === "facilityId") return "facility";
  if (key === "state") return "state";
  if (key === "county") return "county";
  if (key === "tags" || key === "facilityTags") {
    return "tags";
  }
  if (key === "stateOfMatter") return "stateOfMatter";
  if (key === "after" || key === "before") return "date";

  return "text";
}

function getTagType(filterKeyType?: FilterKeyType): TagType | undefined {
  switch (filterKeyType) {
    case "facilityTags":
      return "facility";
    case "taskTags":
      return "task";
    case "workflowTags":
      return "workflow";
    case "wasteTags":
      return "waste";
    default:
      return undefined;
  }
}
// Generate filter sections from columnFilterKeys
function generateFilterSections(
  columnFilterKeys: FilterKey[],
  filterGroups?: FilterGroup[]
): FilterSection[] {
  const fields = columnFilterKeys.map((filterKey) => ({
    key: filterKey.key,
    label: filterKey.header,
    type: getFieldType(filterKey.key, filterKey.filterKeyType),
    enumValues: filterKey.enumValues,
    enumPresentationFunction: filterKey.enumPresentationFunction,
    // Preserve the original filterKeyType to determine tag type
    tagType: getTagType(filterKey.filterKeyType),
    dateRangeShortcuts: filterKey.dateRangeShortcuts,
    disableFilterWhen: filterKey.disableFilterWhen,
    nullCheckboxLabel: filterKey.nullCheckboxLabel,
  }));

  // If filterGroups are provided, use them to organize the fields
  if (filterGroups && filterGroups.length > 0) {
    const sections: FilterSection[] = [];
    const usedFieldKeys = new Set<string>();

    // Create sections based on filterGroups
    filterGroups.forEach((group) => {
      const groupFields = fields.filter((field) => {
        if (group.fields.includes(field.key)) {
          usedFieldKeys.add(field.key);
          return true;
        }
        // Also check for customerFacilityId mapping to facility group
        if (
          group.fields.includes("facility") &&
          field.key === "customerFacilityId"
        ) {
          usedFieldKeys.add(field.key);
          return true;
        }
        return false;
      });

      if (groupFields.length > 0) {
        sections.push({
          title: group.name,
          fields: groupFields,
        });
      }
    });

    // Add any remaining fields to "Other Filters" section
    const otherFields = fields.filter((field) => !usedFieldKeys.has(field.key));
    if (otherFields.length > 0) {
      sections.push({
        title: "🧰 Other Filters",
        fields: otherFields,
      });
    }

    return sections;
  }

  // If no filterGroups provided, return ungrouped list
  return [
    {
      title: "",
      fields,
    },
  ];
}

export function parseOmnisearch(
  omnisearch: string,
  keyAliases: Record<string, string> = {}
): InputValues {
  // Always use normalized parsing - ignores aliases for consistent internal representation
  return parseOmnisearchNormalized(omnisearch);
}

export function stringifyOmnisearch(
  inputValues: InputValues,
  keyAliases: Record<string, string> = {}
): string {
  // Always output with aliases if provided
  return stringifyOmnisearchWithAliases(inputValues, keyAliases);
}

// New function: Parse omnisearch to normalized internal representation
function parseOmnisearchNormalized(omnisearch: string): InputValues {
  const parsed: InputValues = {};
  const keyValuePairs = extractKeyValuePairs(omnisearch);

  keyValuePairs.forEach(({ key, value }) => {
    // Normalize the key to its canonical form
    const canonicalKey = getCanonicalKey(key);

    // Special handling for tags:null (untagged filter)
    // Preserve as string "null" rather than array ["null"]
    if (
      (canonicalKey === "tags" || canonicalKey.endsWith("Tags")) &&
      value.length === 1 &&
      value[0] === "null"
    ) {
      parsed[canonicalKey] = "null";
    } else if (
      // Special handling for specific fields remains the same
      canonicalKey === "stateOfMatter" &&
      value.length === 1 &&
      value[0].includes("|")
    ) {
      parsed[canonicalKey] = value[0].split("|").map((v) => v.trim());
    } else {
      // Handle duplicate keys by combining values
      if (parsed[canonicalKey]) {
        // If key already exists, combine the values
        const existingValues = Array.isArray(parsed[canonicalKey])
          ? parsed[canonicalKey]
          : [parsed[canonicalKey]];
        const newValues = Array.isArray(value) ? value : [value];
        const deduped = new Set([...existingValues, ...newValues]);
        parsed[canonicalKey] = Array.from(deduped);
      } else {
        parsed[canonicalKey] = value;
      }
    }
  });

  return parsed;
}

// New function: Stringify from normalized representation with aliases
function stringifyOmnisearchWithAliases(
  inputValues: InputValues,
  keyAliases: Record<string, string> = {}
): string {
  const parts: string[] = [];

  Object.entries(inputValues).forEach(([key, value]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return;

    // TODO: This does not look correct to me to assume that anything like {id: xxx} is a facility.
    // Handle FacilityPickerFragment - check for any object with an id property
    if (
      value &&
      typeof value === "object" &&
      "id" in value &&
      typeof value.id === "string"
    ) {
      // Always convert to facilityId for backend consistency
      parts.push(`facilityId:${value.id}`);
      return;
    }

    // Handle MultipleFacilityPickerFragment array - key is "facility" or "facilityId" and value is array of objects with id properties
    if (
      (key === "facility" || key === "facilityId") &&
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      "id" in value[0]
    ) {
      // Treat as MultipleFacilityPickerFragment array
      const facilityIds = (value as any[]).map((facility: any) => facility.id);
      parts.push(`facilityId:${facilityIds.join("|")}`);
      return;
    }

    // Handle MultipleTenantPicker array - key is "tenant", "tenantId", or "tenantName" and value is array of objects with id properties
    if (
      (key === "tenant" || key === "tenantId" || key === "tenantName") &&
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      "id" in value[0]
    ) {
      // Treat as MultipleTenantPicker array
      const tenantIds = (value as any[]).map((tenant: any) => tenant.id);
      // Use the appropriate key based on what was provided
      const outputKey = key === "tenantName" ? "tenantName" : "tenantId";
      parts.push(`${outputKey}:${tenantIds.join("|")}`);
      return;
    }

    // Handle MultipleUserPicker array - value is array of objects with email properties
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      "email" in value[0]
    ) {
      // Treat as MultipleUserPicker array - stringify to emails
      const emails = (value as UserOption[]).map((user) => user.email);
      parts.push(`${key}:${emails.join("|")}`);
      return;
    }

    // Handle stateOfMatter with pipe separators
    if (key === "stateOfMatter" && Array.isArray(value)) {
      parts.push(`${key}:${value.join("|")}`);
      return;
    }

    // Handle date ranges
    if (typeof value === "string" && value.includes("..")) {
      parts.push(`${key}:${value}`);
      return;
    }

    // Handle special "null" value for tags (untagged filter)
    if ((key === "tags" || key.endsWith("Tags")) && value === "null") {
      parts.push(`${key}:null`);
      return;
    }

    // Apply aliases for output
    const outputKey = keyAliases[key] || key;

    if (Array.isArray(value)) {
      const filtered = value.filter((v) => v);
      if (filtered.length > 0) {
        if (key === "facilityIds") {
          parts.push(`${outputKey}:"${filtered.join(",")}"`);
        } else {
          const formattedValues = filtered.map((v) => {
            if (v.includes(" ") || v.includes(",") || v.includes("|")) {
              return `"${v}"`;
            }
            return v;
          });
          parts.push(`${outputKey}:${formattedValues.join(",")}`);
        }
      }
    } else if (value && typeof value === "string") {
      if (value.includes(" ") || value.includes(",")) {
        parts.push(`${outputKey}:"${value}"`);
      } else {
        parts.push(`${outputKey}:${value}`);
      }
    }
  });

  const result = parts.join(" ");
  return result;
}

export type FilterPopoverProps = {
  isOpen: boolean;
  filterButtonAnchor: HTMLButtonElement | null;
  handleCloseFilter: () => void;
  columnFilterKeys: FilterKey[];
  onApply: (omnisearch: string) => void;
  omnisearch: string;
  savedFilters: {
    name: string;
    query: string;
    source?: "current" | "legacy-favorites";
  }[];
  onSavePreset: (name: string, query: string) => void;
  onDeletePreset: (name: string) => void;
  filterGroups?: FilterGroup[];
  keyAliases?: Record<string, string>;
  /** Optional tenantId for backend queries. */
  tenantId?: string;
  /** Opt-in for wide (3-column) filter layout. */
  wideLayout?: boolean;
};

// Simple state picker for filters
function SimpleStatePicker({
  value,
  onChange,
  size = "small",
  sx,
  onKeyDown,
  onOpen,
  onClose,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  size?: "small" | "medium";
  sx?: any;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const stateOptions = useMemo(
    () => sortBy(Object.entries(STATE_NAMES), 1).map(([abbr, name]) => abbr),
    []
  );

  // Normalize input values to proper state abbreviations
  const normalizeStateValue = (inputValue: string): string | null => {
    if (!inputValue) return null;
    const abbr = inputValue.trim().toUpperCase();
    return STATE_NAMES[abbr as keyof typeof STATE_NAMES] ? abbr : null;
  };

  // Convert value (array of abbreviations) to array of valid abbreviations for Autocomplete
  const selectedStates = useMemo(
    () =>
      Array.isArray(value)
        ? value.map(normalizeStateValue).filter((v): v is string => v !== null)
        : [],
    [value]
  );

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    changeValue: (string | null)[]
  ) => {
    onChange(changeValue.filter((v): v is string => v !== null).sort());
  };

  return (
    <FormControl fullWidth>
      <Autocomplete
        size={size}
        multiple
        options={stateOptions}
        value={selectedStates}
        onChange={handleChange}
        onOpen={onOpen}
        onClose={onClose}
        getOptionLabel={(abbr) =>
          `${STATE_NAMES[abbr as keyof typeof STATE_NAMES]} (${abbr})`
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="State"
            autoComplete="off"
            onKeyDown={onKeyDown}
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
          />
        )}
      />
    </FormControl>
  );
}

// Helper: Normalize input values for comparison (sort arrays, etc.)
function normalizeInputValues(input: InputValues): InputValues {
  const normalized: InputValues = {};
  Object.entries(input).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      normalized[key] = [...value].sort();
    } else if (typeof value === "object" && value !== null && "id" in value) {
      // For FacilityPickerFragment, just use the id string for comparison
      normalized[key] = value.id as string;
    } else {
      normalized[key] = value;
    }
  });
  return normalized;
}

// Helper: Deep compare two InputValues (order-insensitive for arrays)
function areFiltersEqual(a: InputValues, b: InputValues): boolean {
  // Normalize both before comparison
  const normA = normalizeInputValues(a);
  const normB = normalizeInputValues(b);

  const keysA = Object.keys(normA).filter(
    (k) =>
      normA[k] && (!Array.isArray(normA[k]) || (normA[k] as any[]).length > 0)
  );
  const keysB = Object.keys(normB).filter(
    (k) =>
      normB[k] && (!Array.isArray(normB[k]) || (normB[k] as any[]).length > 0)
  );
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!(key in normB)) return false;
    const valA = normA[key];
    const valB = normB[key];
    if (Array.isArray(valA) && Array.isArray(valB)) {
      if (valA.length !== valB.length) return false;
      for (let i = 0; i < valA.length; i++) {
        if (valA[i] !== valB[i]) return false;
      }
    } else if (
      typeof valA === "object" &&
      valA !== null &&
      typeof valB === "object" &&
      valB !== null
    ) {
      // Should not happen after normalization, but fallback to JSON stringify
      if (JSON.stringify(valA) !== JSON.stringify(valB)) return false;
    } else {
      if (valA !== valB) return false;
    }
  }
  return true;
}

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  isOpen,
  filterButtonAnchor,
  handleCloseFilter,
  columnFilterKeys,
  onApply,
  omnisearch,
  savedFilters,
  onSavePreset,
  onDeletePreset,
  filterGroups = [],
  keyAliases = {},
  tenantId,
  wideLayout = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [inputValues, setInputValues] = useState<InputValues>(
    parseOmnisearch(omnisearch ?? "", keyAliases)
  );
  const [saveAsPreset, setSaveAsPreset] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [selectedSavedFilter, setSelectedSavedFilter] = useState("");
  const [expandSavedFilters, setExpandSavedFilters] = useState(false);

  // Ref to access Popover's updatePosition method
  const popoverActions = useRef<{ updatePosition: () => void }>(null);

  // Track count of open dropdowns to prevent Enter key from closing the dialog
  // Use a counter to handle cases where multiple dropdowns might be open
  const openDropdownCountRef = useRef(0);

  const prevIsMobile = useRef(isMobile);

  // Update popover position when mobile breakpoint changes
  useEffect(() => {
    if (prevIsMobile.current !== isMobile) {
      // Just track the change, no need for transition state
    }
    prevIsMobile.current = isMobile;

    if (isOpen && popoverActions.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        popoverActions.current?.updatePosition();
      }, 100);
    }
  }, [isMobile, isOpen]);

  // Compute which saved filter is currently selected based on parsed values
  const selectedFilterName = useMemo(() => {
    const currentNormalized = inputValues;

    const matchingFilter = savedFilters.find((f) => {
      // Parse saved filter with normalization
      const savedNormalized = parseOmnisearchNormalized(f.query);
      return areFiltersEqual(currentNormalized, savedNormalized);
    });

    return matchingFilter?.name || "";
  }, [inputValues, savedFilters]);

  // Prevent body scrolling on mobile when popover is open
  useEffect(() => {
    if (isOpen && isMobile) {
      // Save current body overflow
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;

      // Prevent scrolling
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      return () => {
        // Restore original styles
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = "";

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, isMobile]);

  // Generate filter sections from columnFilterKeys
  const filterSections = useMemo(
    () => generateFilterSections(columnFilterKeys, filterGroups),
    [columnFilterKeys, filterGroups]
  );

  // Expand the saved filters expander if a saved filter is already applied
  useEffect(() => {
    if (selectedSavedFilter) {
      setExpandSavedFilters(true);
    }
  }, [selectedSavedFilter]);

  useEffect(() => {
    // Only reset inputValues when the popover is first opened or when omnisearch changes externally
    // Don't reset if the user has made local changes
    if (!isOpen) {
      setInputValues(parseOmnisearch(omnisearch ?? "", keyAliases));
    }

    // Check if current omnisearch matches any saved filter (order-insensitive for arrays)
    if (omnisearch) {
      const currentParsed = parseOmnisearch(omnisearch ?? "", keyAliases);
      const matchingFilter = savedFilters.find((f) => {
        const savedParsed = parseOmnisearch(f.query, keyAliases);
        return areFiltersEqual(currentParsed, savedParsed);
      });
      if (matchingFilter) {
        setSelectedSavedFilter(matchingFilter.query);
      } else {
        setSelectedSavedFilter("");
      }
    } else {
      setSelectedSavedFilter("");
    }
  }, [omnisearch, savedFilters, keyAliases, isOpen]);

  // Clear preset name when input values change (unless they match a saved filter)
  useEffect(() => {
    if (saveAsPreset) {
      const currentOmnisearch = stringifyOmnisearch(inputValues, keyAliases);
      const currentParsed = parseOmnisearch(currentOmnisearch, keyAliases);
      const matchingFilter = savedFilters.find((f) => {
        const savedParsed = parseOmnisearch(f.query, keyAliases);
        return areFiltersEqual(currentParsed, savedParsed);
      });
      if (!matchingFilter) {
        setPresetName("");
      }
    }

    // Also update selected saved filter when input values change (order-insensitive for arrays)
    const currentOmnisearch = stringifyOmnisearch(inputValues, keyAliases);
    const currentParsed = parseOmnisearch(currentOmnisearch, keyAliases);
    if (currentOmnisearch) {
      const matchingFilter = savedFilters.find((f) => {
        const savedParsed = parseOmnisearch(f.query, keyAliases);
        return areFiltersEqual(currentParsed, savedParsed);
      });
      if (matchingFilter) {
        setSelectedSavedFilter(matchingFilter.query);
      } else {
        setSelectedSavedFilter("");
      }
    } else {
      setSelectedSavedFilter("");
    }
  }, [inputValues, saveAsPreset, savedFilters, keyAliases]);

  const initialOmnisearchRef = useRef("");
  const initialSaveAsPresetRef = useRef(false);
  const initialPresetNameRef = useRef("");
  const initialInputValuesRef = useRef<InputValues>({});

  // Track last applied state
  const lastAppliedInputValuesRef = useRef<InputValues>(
    parseOmnisearch(omnisearch ?? "", keyAliases)
  );
  const lastAppliedSaveAsPresetRef = useRef<boolean>(false);
  const lastAppliedPresetNameRef = useRef<string>("");

  useEffect(() => {
    if (isOpen) {
      initialOmnisearchRef.current = stringifyOmnisearch(
        parseOmnisearch(omnisearch ?? "", keyAliases),
        keyAliases
      );
      initialSaveAsPresetRef.current = saveAsPreset;
      initialPresetNameRef.current = presetName;
      initialInputValuesRef.current = parseOmnisearch(
        omnisearch ?? "",
        keyAliases
      );

      // When opening, set last applied state to current
      lastAppliedInputValuesRef.current = parseOmnisearch(
        omnisearch ?? "",
        keyAliases
      );
      lastAppliedSaveAsPresetRef.current = false;
      lastAppliedPresetNameRef.current = "";
    }
  }, [isOpen, omnisearch, keyAliases, saveAsPreset, presetName]);

  // Helper function to check if a field should be disabled based on dependencies
  const isFieldDisabled = useCallback(
    (field: FilterField): boolean => {
      if (!field.disableFilterWhen) return false;

      const { field: dependentField, value: disablingValue } =
        field.disableFilterWhen;
      const currentValue = inputValues[dependentField];

      // Only support simple scalar values (string, boolean) for the disabling field
      // This covers boolean filters, enum filters, and other simple select-based filters
      // Complex types like date ranges or arrays are not supported as disabling fields
      return String(currentValue) === String(disablingValue);
    },
    [inputValues]
  );

  // Helper to get the appropriate zero value for a field based on its current value type
  const getZeroValue = useCallback((currentValue: any): any => {
    // If current value is already an array, clear to empty array
    if (Array.isArray(currentValue)) {
      return [];
    }

    // For object types (like facility objects), clear to null
    if (currentValue !== null && typeof currentValue === "object") {
      return null;
    }

    // For primitive types, clear to empty string
    return "";
  }, []);

  // Auto-clear fields once they are disabled
  useEffect(() => {
    columnFilterKeys
      .filter((filterKey) => filterKey.disableFilterWhen)
      .forEach((filterKey) => {
        const field: FilterField = {
          key: filterKey.key,
          type: filterKey.filterKeyType,
          disableFilterWhen: filterKey.disableFilterWhen,
        } as FilterField;

        if (isFieldDisabled(field)) {
          // If the field is disabled and has a value, clear it
          const currentValue = inputValues[field.key];
          const hasValue = Array.isArray(currentValue)
            ? currentValue.length > 0
            : currentValue !== "" &&
              currentValue !== undefined &&
              currentValue !== null;

          if (hasValue) {
            const zeroValue = getZeroValue(currentValue);
            setInputValues((prev) => ({
              ...prev,
              [field.key]: zeroValue,
            }));
          }
        }
      });
  }, [inputValues, columnFilterKeys, isFieldDisabled, getZeroValue]);

  const isApplyDisabled = useMemo(() => {
    const filtersUnchanged = areFiltersEqual(
      inputValues,
      lastAppliedInputValuesRef.current
    );
    if (!saveAsPreset && filtersUnchanged) {
      return true;
    }
    if (saveAsPreset && !presetName.trim()) {
      return true;
    }
    return false;
  }, [inputValues, saveAsPreset, presetName]);

  const handleApply = useCallback(() => {
    // Get normalized representation for storage
    const normalizedInputValues = { ...inputValues };

    // Parse the current omnisearch to extract non-filter content
    const currentParsed = parseOmnisearch(omnisearch ?? "", keyAliases);

    // Get the keys that are managed by this filter popover
    const managedKeys = new Set(
      filterSections.flatMap((section) =>
        section.fields.map((field) => getCanonicalKey(field.key))
      )
    );

    // Preserve any existing omnisearch content that ISN'T managed by the filter popover
    // (e.g., text search terms, other filters not in this popover)
    const preservedValues: InputValues = {};
    Object.entries(currentParsed).forEach(([key, value]) => {
      const canonicalKey = getCanonicalKey(key);
      if (!managedKeys.has(canonicalKey)) {
        preservedValues[canonicalKey] = value;
      }
    });

    // Combine preserved values with new filter values
    // This REPLACES any managed keys (allowing removal) while preserving unmanaged content
    const mergedValues = { ...preservedValues, ...normalizedInputValues };

    // Create omnisearch string without aliases for storage
    const storageOmnisearch = stringifyOmnisearchWithAliases(mergedValues, {});

    // Create display omnisearch with aliases for URL
    const displayOmnisearch = stringifyOmnisearchWithAliases(
      mergedValues,
      keyAliases
    );

    // If save as preset is checked and preset name is provided
    if (saveAsPreset && onSavePreset && presetName.trim()) {
      // Save preset with normalized version (no aliases)
      onSavePreset(presetName.trim(), storageOmnisearch);
      setSaveAsPreset(false);
      setPresetName("");
    }

    // Update last applied state
    lastAppliedInputValuesRef.current = normalizedInputValues;
    lastAppliedSaveAsPresetRef.current = false;
    lastAppliedPresetNameRef.current = "";

    // Apply with aliased version for display
    onApply(displayOmnisearch);
    handleCloseFilter();
  }, [
    handleCloseFilter,
    inputValues,
    omnisearch,
    onApply,
    saveAsPreset,
    onSavePreset,
    presetName,
    keyAliases,
    filterSections,
  ]);

  const handleSavedFilterSelect = useCallback(
    (filterQuery: string) => {
      // Always parse the filter query, even if it's empty
      const parsedValues = parseOmnisearch(filterQuery, keyAliases);
      setInputValues(parsedValues);
      setExpandSavedFilters(true);
    },
    [savedFilters, saveAsPreset, keyAliases]
  );

  const handleSaveAsPresetChange = useCallback((checked: boolean) => {
    setSaveAsPreset(checked);
    if (checked) {
      setPresetName("");
    }
  }, []);

  const handleDeleteFilter = useCallback(
    (filterName: string) => {
      if (onDeletePreset) {
        onDeletePreset(filterName);
        // Clear selection if deleting the currently selected filter
        if (
          selectedSavedFilter &&
          savedFilters.find(
            (f) => f.name === filterName && f.query === selectedSavedFilter
          )
        ) {
          setSelectedSavedFilter("");
        }
      }
    },
    [onDeletePreset, selectedSavedFilter, savedFilters]
  );

  const handleSelectFilter = useCallback(
    (filterQuery: string) => {
      setSelectedSavedFilter(filterQuery);
      handleSavedFilterSelect(filterQuery);
      setExpandSavedFilters(true);
    },
    [handleSavedFilterSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        // Only handle Enter if no dropdown is open
        // If dropdown is open, let MUI handle the Enter key to select the option
        if (openDropdownCountRef.current === 0) {
          e.preventDefault();
          handleApply();
        }
      }
    },
    [handleApply]
  );

  // Callbacks to track dropdown open/close state
  // Use a counter to handle cases where multiple dropdowns might be open
  const handleDropdownOpen = useCallback(() => {
    openDropdownCountRef.current += 1;
  }, []);

  const handleDropdownClose = useCallback(() => {
    openDropdownCountRef.current = Math.max(
      0,
      openDropdownCountRef.current - 1
    );
  }, []);

  const handleCloseAndResetPreset = useCallback(() => {
    setSaveAsPreset(false);
    setPresetName("");
    if (savedFilters.length === 0) {
      setExpandSavedFilters(false);
    }
    handleCloseFilter();
  }, [handleCloseFilter, savedFilters.length]);

  const renderField = (field: FilterField) => {
    const standardInputProps = {
      size: "small" as const,
      fullWidth: true,
      sx: {
        "& .MuiInputBase-root": {
          height: "40px", // Standardize height across all inputs
        },
      },
    };

    const isDisabled = isFieldDisabled(field);

    switch (field.type) {
      case "facility":
      case "wasteFacility":
        return (
          <FacilityPicker
            defaultSearchTerm={tenantId ? `tenantId:${tenantId}` : undefined}
            facilityQueryVariant={
              field.type === "wasteFacility" ? "waste" : "all"
            }
            value={
              (inputValues[field.key] as FacilityPickerFragment | null) || null
            }
            onChange={(value: FacilityPickerFragment | null) =>
              setInputValues((prev) => ({ ...prev, [field.key]: value }))
            }
            lazyLoad={false}
            showErrors={false}
            error={undefined}
            invalid={false}
            isTouched={false}
            isDirty={false}
            isValidating={false}
            disabled={isDisabled}
            onOpen={handleDropdownOpen}
            onClose={handleDropdownClose}
            textFieldProps={{
              ...standardInputProps,
              onKeyDown: handleKeyDown,
              sx: {
                ...standardInputProps.sx,
                "& .MuiInputBase-root": {
                  height: "40px",
                },
              },
            }}
          />
        );

      case "multipleFacility":
      case "wasteMultipleFacility": {
        // Handle both full facility objects and ID-only inputs from omnisearch
        const facilityValue = inputValues[getCanonicalKey(field.key)];
        let processedValue: (MultipleFacilityPickerFragment | string)[] | null =
          null;

        if (Array.isArray(facilityValue)) {
          // If it's an array, it could be facility objects or ID strings
          processedValue = facilityValue;
        } else if (typeof facilityValue === "string") {
          // If it's a string, it might be pipe-separated IDs from omnisearch parsing
          processedValue = facilityValue.split("|").filter((id) => id.trim());
        }

        return (
          <MultipleFacilityPicker
            defaultSearchTerm={tenantId ? `tenantId:${tenantId}` : undefined}
            facilityQueryVariant={
              field.type === "wasteMultipleFacility" ? "waste" : "all"
            }
            value={processedValue}
            onChange={(value: MultipleFacilityPickerFragment[] | null) =>
              setInputValues((prev) => ({
                ...prev,
                [getCanonicalKey(field.key)]: value as any,
              }))
            }
            disabled={isDisabled}
            onOpen={handleDropdownOpen}
            onClose={handleDropdownClose}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "wrap",
              },
              "& .MuiChip-root": {
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        );
      }

      case "multipleTenant": {
        // Handle both full tenant objects and ID-only inputs from omnisearch
        const tenantValue = inputValues[getCanonicalKey(field.key)];
        let processedValue: (TenantOption | string)[] | null = null;

        if (Array.isArray(tenantValue)) {
          // If it's an array, it could be tenant objects or ID strings
          processedValue = tenantValue;
        } else if (typeof tenantValue === "string") {
          // If it's a string, it might be pipe-separated IDs from omnisearch parsing
          processedValue = tenantValue.split("|").filter((id) => id.trim());
        }

        return (
          <MultipleTenantPicker
            label={field.label}
            value={processedValue}
            onChange={(value: TenantOption[] | null) => {
              setInputValues((prev) => ({
                ...prev,
                [getCanonicalKey(field.key)]: value,
              }));
            }}
            lazyLoad={false}
            disabled={isDisabled}
            invalid={false}
            isTouched={false}
            isDirty={false}
            isValidating={false}
            onOpen={handleDropdownOpen}
            onClose={handleDropdownClose}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "wrap",
              },
              "& .MuiChip-root": {
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        );
      }

      case "multipleUser": {
        // Handle both full user objects and email-only inputs from omnisearch
        const userValue = inputValues[getCanonicalKey(field.key)];
        let processedValue: (UserOption | string)[] | null = null;

        if (Array.isArray(userValue)) {
          // If it's an array, it could be user objects or email strings
          processedValue = userValue;
        } else if (typeof userValue === "string") {
          // If it's a string, it might be pipe-separated emails from omnisearch parsing
          processedValue = userValue.split("|").filter((email) => email.trim());
        }

        return (
          <MultipleUserPicker
            label={field.label}
            value={processedValue}
            onChange={(value: UserOption[] | null) => {
              setInputValues((prev) => ({
                ...prev,
                [getCanonicalKey(field.key)]: value,
              }));
            }}
            lazyLoad={false}
            disabled={isDisabled}
            onOpen={handleDropdownOpen}
            onClose={handleDropdownClose}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "wrap",
              },
              "& .MuiChip-root": {
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        );
      }

      case "state":
        return (
          <SimpleStatePicker
            value={(inputValues[field.key] as string[]) || []}
            onChange={(value: string[]) =>
              setInputValues((prev) => ({ ...prev, [field.key]: value }))
            }
            size="small"
            sx={{
              ...standardInputProps.sx,
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "wrap",
              },
              "& .MuiChip-root": {
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              ...(isDisabled && {
                pointerEvents: "none",
                opacity: 0.6,
              }),
            }}
            onKeyDown={handleKeyDown}
            onOpen={handleDropdownOpen}
            onClose={handleDropdownClose}
          />
        );

      case "county":
        return (
          <TextField
            {...standardInputProps}
            label={field.label}
            value={(inputValues[field.key] as string) || ""}
            onChange={(e) =>
              setInputValues((prev) => ({
                ...prev,
                [field.key]: e.target.value,
              }))
            }
            onKeyDown={handleKeyDown}
            placeholder="e.g., Maricopa"
            disabled={isDisabled}
            autoComplete="off"
            inputProps={{
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
          />
        );

      case "tags": {
        // Use canonical key lookup since parseOmnisearch normalizes keys
        // e.g., "facilityTags" in omnisearch becomes "tags" in inputValues
        const canonicalKey = getCanonicalKey(field.key);
        const tagValue = inputValues[canonicalKey] ?? inputValues[field.key];
        const isUntaggedOnly = tagValue === "null";
        return (
          <Stack
            spacing={1}
            sx={{
              ...(isDisabled && {
                pointerEvents: "none",
                opacity: 0.6,
              }),
            }}
          >
            <Box
              sx={{
                opacity: isUntaggedOnly ? 0.5 : 1,
                pointerEvents: isUntaggedOnly ? "none" : "auto",
              }}
            >
              <UnifiedTagsInput
                value={isUntaggedOnly ? [] : (tagValue as string[]) || []}
                onChange={(value: string[]) => {
                  // Store under the field's actual key so it gets serialized correctly
                  setInputValues((prev) => ({ ...prev, [field.key]: value }));
                }}
                label={field.label}
                onRequestApply={handleApply}
                tagsQuery={field.tagType ? TAGS_QUERY : undefined}
                queryConfig={buildTagQueryConfig({
                  tagType: field.tagType,
                  tenantId,
                })}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUntaggedOnly}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInputValues((prev) => ({
                        ...prev,
                        [field.key]: "null",
                      }));
                    } else {
                      setInputValues((prev) => ({
                        ...prev,
                        [field.key]: [],
                      }));
                    }
                  }}
                  size="small"
                />
              }
              label="Untagged only"
              sx={{ ml: 0, mt: -0.5 }}
            />
          </Stack>
        );
      }

      case "facilityTags":
        // This case shouldn't be reached since getFieldType treats facilityTags as tags
        // But keeping it for safety
        return (
          <Box
            sx={{
              ...(isDisabled && {
                pointerEvents: "none",
                opacity: 0.6,
              }),
            }}
          >
            <UnifiedTagsInput
              value={(inputValues[field.key] as string[]) || []}
              onChange={(value: string[]) =>
                setInputValues((prev) => ({ ...prev, [field.key]: value }))
              }
              label={field.label}
              onRequestApply={handleApply}
            />
          </Box>
        );

      case "boolean":
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              size="small"
              value={(inputValues[field.key] as string) || ""}
              onChange={(e) =>
                setInputValues((prev) => ({
                  ...prev,
                  [field.key]: e.target.value,
                }))
              }
              onOpen={handleDropdownOpen}
              onClose={handleDropdownClose}
              label={field.label}
              disabled={isDisabled}
              autoComplete="off"
              inputProps={{
                autoComplete: "off",
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
              sx={{
                height: "40px", // Match the height of other inputs
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                  py: 0,
                },
              }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        );

      case "enum":
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              size="small"
              value={(inputValues[field.key] as string) || ""}
              onChange={(e) =>
                setInputValues((prev) => ({
                  ...prev,
                  [field.key]: e.target.value,
                }))
              }
              onOpen={handleDropdownOpen}
              onClose={handleDropdownClose}
              label={field.label}
              disabled={isDisabled}
              autoComplete="off"
              inputProps={{
                autoComplete: "off",
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
              sx={{
                height: "40px", // Match the height of other inputs
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                  py: 0,
                },
              }}
            >
              <MenuItem value="" sx={{ fontStyle: "italic" }}>
                Clear
              </MenuItem>
              {field.enumValues?.map((value) => (
                <MenuItem key={value} value={value}>
                  {field.enumPresentationFunction
                    ? field.enumPresentationFunction(value)
                    : prettyPrintEnumValue(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "enumAutocomplete": {
        const currentValue = inputValues[field.key];
        const selectedEnumValues = currentValue
          ? typeof currentValue === "string"
            ? currentValue.split("|")
            : Array.isArray(currentValue)
            ? currentValue
            : []
          : [];

        return (
          <FormControl fullWidth>
            <Autocomplete
              multiple
              size="small"
              options={field.enumValues || []}
              value={selectedEnumValues}
              onChange={(_, newValue) => {
                setInputValues((prev) => ({
                  ...prev,
                  [field.key]: newValue.join("|"),
                }));
              }}
              onOpen={handleDropdownOpen}
              onClose={handleDropdownClose}
              disabled={isDisabled}
              getOptionLabel={(option) =>
                field.enumPresentationFunction
                  ? field.enumPresentationFunction(option)
                  : prettyPrintEnumValue(option)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.label}
                  placeholder={
                    selectedEnumValues.length === 0 ? "Select..." : ""
                  }
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={
                        field.enumPresentationFunction
                          ? field.enumPresentationFunction(option)
                          : prettyPrintEnumValue(option)
                      }
                      {...chipProps}
                    />
                  );
                })
              }
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  flexWrap: "wrap",
                },
                "& .MuiChip-root": {
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              slotProps={{
                popper: {
                  style: { zIndex: 1400 },
                },
              }}
            />
          </FormControl>
        );
      }

      case "number": {
        const numberInputValue = inputValues[field.key];
        const numberValue = Array.isArray(numberInputValue)
          ? numberInputValue
          : [];
        const currentOperator =
          numberValue.length > 0 &&
          (numberValue[0].startsWith(">") || numberValue[0].startsWith("<"))
            ? numberValue[0].charAt(0)
            : "=";
        const currentNumber =
          numberValue.length > 0 ? numberValue[0].replace(/^[<>=]/, "") : "";

        return (
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <Select
                  size="small"
                  value={currentOperator}
                  onChange={(e) => {
                    const newOperator = e.target.value;
                    if (currentNumber) {
                      const newValue =
                        newOperator === "="
                          ? currentNumber
                          : `${newOperator}${currentNumber}`;
                      setInputValues((prev) => ({
                        ...prev,
                        [field.key]: [newValue],
                      }));
                    } else if (newOperator !== "=") {
                      setInputValues((prev) => ({
                        ...prev,
                        [field.key]: [newOperator],
                      }));
                    }
                  }}
                  onOpen={handleDropdownOpen}
                  onClose={handleDropdownClose}
                  disabled={isDisabled}
                  autoComplete="off"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                  }}
                  sx={{
                    height: "40px",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    "& fieldset": {
                      borderRight: 0,
                    },
                  }}
                >
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value=">">{">"}</MenuItem>
                  <MenuItem value="<">{"<"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={9}>
              <TextField
                {...standardInputProps}
                label={field.label}
                type="number"
                value={currentNumber}
                onChange={(e) => {
                  const newNumber = e.target.value;
                  if (newNumber) {
                    const newValue =
                      currentOperator === "="
                        ? newNumber
                        : `${currentOperator}${newNumber}`;
                    setInputValues((prev) => ({
                      ...prev,
                      [field.key]: [newValue],
                    }));
                  } else {
                    setInputValues((prev) => ({
                      ...prev,
                      [field.key]: [],
                    }));
                  }
                }}
                onKeyDown={handleKeyDown}
                disabled={isDisabled}
                autoComplete="off"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                }}
                sx={{
                  ...standardInputProps.sx,
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    "& fieldset": {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        );
      }

      case "stateOfMatter":
        return (
          <StateOfMatterPicker
            value={
              Array.isArray(inputValues[field.key])
                ? (inputValues[field.key] as string[])
                : []
            }
            onChange={(value: string[]) =>
              setInputValues((prev) => ({ ...prev, [field.key]: value }))
            }
          />
        );

      case "time": {
        const timeInputValue = inputValues[field.key];
        const timeValue = Array.isArray(timeInputValue) ? timeInputValue : [];
        return (
          <TimeFilter
            value={timeValue.length > 0 ? timeValue[0] : ""}
            id={field.key}
            header={field.label}
            onChange={(value) =>
              setInputValues((prev) => ({ ...prev, [field.key]: [value] }))
            }
            handleApply={handleApply}
          />
        );
      }

      case "date": {
        const dateInputValue = inputValues[field.key];
        const dateValueArray = Array.isArray(dateInputValue)
          ? dateInputValue
          : [];
        const dateValue =
          dateValueArray.length > 0
            ? dateValueArray[0]
            : typeof dateInputValue === "string"
            ? dateInputValue
            : "";

        // For single date fields (after, before), use DatePicker
        if (field.key === "after" || field.key === "before") {
          let singleDateValue: DateTime | null = null;

          if (typeof dateValue === "string" && dateValue.trim() !== "") {
            const parsed = DateTime.fromISO(dateValue);
            // Only use the parsed value if it's valid
            if (parsed.isValid) {
              singleDateValue = parsed;
            }
          }

          return (
            <DatePicker<DateTime>
              label={field.label}
              value={singleDateValue}
              onChange={(newValue) => {
                if (newValue) {
                  setInputValues((prev) => ({
                    ...prev,
                    [field.key]: [newValue.toISODate()],
                  }));
                } else {
                  setInputValues((prev) => ({
                    ...prev,
                    [field.key]: [],
                  }));
                }
              }}
              onOpen={handleDropdownOpen}
              onClose={handleDropdownClose}
              disabled={isDisabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: standardInputProps.sx,
                  onKeyDown: handleKeyDown,
                  autoComplete: "off",
                  error: false,
                  inputProps: {
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                  },
                },
              }}
            />
          );
        }

        // For date range fields, use DateRangePicker
        let dateValues: [DateTime | null, DateTime | null] = [null, null];
        if (typeof dateValue === "string" && dateValue.includes("..")) {
          const [start, end] = dateValue.split("..");
          dateValues = [
            start ? DateTime.fromISO(start) : null,
            end ? DateTime.fromISO(end) : null,
          ];
        }

        // Check if shortcuts should be enabled (only when explicitly true)
        const enableShortcuts = field.dateRangeShortcuts === true;

        return (
          <DateRangePicker<DateTime>
            calendars={2}
            localeText={{
              start: `${field.label} Start`,
              end: `${field.label} End`,
            }}
            value={dateValues}
            onChange={(newValue) => {
              const [start, end] = newValue;
              if (start || end) {
                const dateRangeString = `${start?.toISODate() || ""}..${
                  end?.toISODate() || ""
                }`;
                setInputValues((prev) => ({
                  ...prev,
                  [field.key]: [dateRangeString],
                }));
              } else {
                setInputValues((prev) => ({
                  ...prev,
                  [field.key]: [],
                }));
              }
            }}
            onOpen={handleDropdownOpen}
            onClose={handleDropdownClose}
            disabled={isDisabled}
            slots={
              enableShortcuts
                ? {
                    field: MultiInputDateRangeField,
                    shortcuts: HorizontalShortcuts,
                  }
                : { field: MultiInputDateRangeField }
            }
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: standardInputProps.sx,
                autoComplete: "off",
                error: false,
                inputProps: {
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                },
              },
              popper: {
                style: { zIndex: 1400 },
              },
              ...(enableShortcuts && {
                shortcuts: { items: datePickerShortcuts },
              }),
            }}
          />
        );
      }

      default: {
        const rawValue = inputValues[field.key];
        const isNullChecked =
          !!field.nullCheckboxLabel &&
          (rawValue === "null" ||
            (Array.isArray(rawValue) &&
              rawValue.length === 1 &&
              rawValue[0] === "null"));
        return (
          <>
            <TextField
              {...standardInputProps}
              label={field.label}
              value={isNullChecked ? "" : (Array.isArray(rawValue) ? rawValue.join(", ") : rawValue) || ""}
              onChange={(e) =>
                setInputValues((prev) => ({
                  ...prev,
                  [field.key]: e.target.value,
                }))
              }
              onKeyDown={handleKeyDown}
              placeholder={field.placeholder}
              disabled={isDisabled || isNullChecked}
              autoComplete="off"
              inputProps={{
                autoComplete: "off",
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
            />
            {field.nullCheckboxLabel && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isNullChecked}
                    onChange={(e) => {
                      setInputValues((prev) => ({
                        ...prev,
                        [field.key]: e.target.checked ? "null" : "",
                      }));
                    }}
                    size="small"
                  />
                }
                label={field.nullCheckboxLabel}
                sx={{ ml: 0, mt: -0.5 }}
              />
            )}
          </>
        );
      }
    }
  };

  return (
    <>
      <Popover
        key={`filter-popover-${isMobile ? "mobile" : "desktop"}`}
        open={isOpen}
        anchorEl={isMobile ? null : filterButtonAnchor}
        onClose={handleCloseAndResetPreset}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        action={popoverActions}
        // Prevent popover from affecting body scroll, which triggers DataGrid's ResizeObserver
        disableScrollLock
        // Full screen on mobile
        {...(isMobile && {
          anchorEl: null,
          anchorOrigin: undefined,
          transformOrigin: undefined,
          BackdropProps: {
            invisible: true,
          },
          marginThreshold: null, // Disable edge detection on mobile
        })}
        PaperProps={{
          sx: {
            ...(isMobile
              ? {
                  // Full screen on mobile - enhanced for small phone sizes
                  position: "fixed !important" as const,
                  top: "0 !important",
                  left: "0 !important",
                  right: "0 !important",
                  bottom: "0 !important",
                  width: "100vw !important",
                  // Remove explicit height, let maxHeight control it
                  maxHeight: "100vh !important",
                  maxWidth: "100vw !important",
                  margin: "0 !important",
                  borderRadius: "0 !important",
                  transform: "none !important",
                  // Additional specificity for small screens
                  ...(isExtraSmall && {
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "100% !important",
                  }),
                }
              : {
                  // Desktop behavior
                  width: wideLayout ? 900 : 460,
                  maxWidth: "90vw",
                  maxHeight: wideLayout
                    ? "min(92vh, calc(100vh - 60px))"
                    : "min(80vh, calc(100vh - 100px))",
                  height: "auto",
                }),
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            // Ensure proper z-index on mobile
            ...(isMobile && {
              zIndex: theme.zIndex.modal + 10,
            }),
          },
        }}
        slotProps={{
          root: isMobile
            ? {
                style: {
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100vw",
                  // Remove explicit height here too
                  maxHeight: "100vh",
                  zIndex: theme.zIndex.modal,
                  margin: 0,
                  padding: 0,
                  // Prevent any scrolling on the root container on mobile only
                  overflow: "hidden",
                  touchAction: "none",
                },
              }
            : undefined,
        }}
      >
        <Stack
          sx={{
            height: "100%",
            position: "relative",
            minHeight: 0, // Critical for flexbox scrolling
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 3,
              py: 2,
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListIcon />
              <Typography variant="h6">
                {isMobile ? "Filters" : "Filter by"}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseAndResetPreset} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Scrollable content area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              minHeight: 0, // Critical for flex child scrolling
              // Only show scrollbar when actually needed
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.action.disabled,
                borderRadius: "3px",
                "&:hover": {
                  background: theme.palette.action.hover,
                },
              },
              // For Firefox
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.action.disabled} transparent`,
              // Mobile-specific scroll behavior
              ...(isMobile && {
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                touchAction: "pan-y",
              }),
            }}
          >
            {/* Saved filters - expandable chip */}
            <Box sx={{ px: 3, pt: 2, pb: 2, flexShrink: 0 }}>
              <Chip
                label="Saved filters"
                onClick={() => setExpandSavedFilters(!expandSavedFilters)}
                onDelete={() => setExpandSavedFilters(!expandSavedFilters)}
                deleteIcon={
                  expandSavedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                sx={{
                  backgroundColor:
                    savedFilters.length > 0 ? "#517bc6" : undefined,
                  color: savedFilters.length > 0 ? "white" : undefined,
                  height: 28,
                  "& .MuiChip-deleteIcon": {
                    color:
                      savedFilters.length > 0
                        ? "white"
                        : theme.palette.text.secondary,
                  },
                  "&:hover": {
                    backgroundColor:
                      savedFilters.length > 0 ? "#4568B0" : undefined,
                  },
                }}
              />

              <Collapse in={expandSavedFilters}>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    maxHeight: 136, // 4 rows * 34px
                    overflow: "auto",
                    backgroundColor: "white",
                  }}
                >
                  {savedFilters.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      No saved filters yet
                    </Typography>
                  ) : (
                    <Table size="small" sx={{ tableLayout: "fixed" }}>
                      <TableBody>
                        {savedFilters.map((filter) => {
                          const isLegacy = filter.source === "legacy-favorites";
                          const isCurrentDataGridFilter =
                            filter.source === "current" ||
                            filter.source === undefined;

                          return (
                            <TableRow
                              key={filter.name}
                              hover
                              onClick={() => handleSelectFilter(filter.query)}
                              selected={selectedFilterName === filter.name}
                              sx={{
                                cursor: "pointer",
                                height: 34,
                                "&:last-child td": { borderBottom: 0 },
                                ...(isLegacy && {
                                  backgroundColor: "action.hover",
                                  opacity: 0.8,
                                }),
                              }}
                            >
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{
                                    ...(isLegacy && {
                                      fontStyle: "italic",
                                      color: "text.secondary",
                                    }),
                                  }}
                                >
                                  {filter.name}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ py: 0.5, px: 1, width: 40 }}
                              >
                                {isCurrentDataGridFilter && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFilter(filter.name);
                                    }}
                                    sx={{ padding: "5px" }}
                                  >
                                    <RemoveIcon
                                      sx={{
                                        fontSize: 20,
                                        color: "action.disabled",
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Collapse>
            </Box>

            {/* Filter sections */}
            <Box sx={{ px: 3, pb: 2, flexGrow: 1 }}>
              {wideLayout ? (
                <Box
                  sx={{
                    columnCount: 3,
                    columnGap: 2.5,
                    "& > *": {
                      breakInside: "avoid",
                      marginBottom: 2,
                    },
                  }}
                >
                  {filterSections.map((section, index) => (
                    <Box key={section.title || index}>
                      <Stack spacing={1}>
                        {section.title && (
                          <Typography
                            variant="subtitle2"
                            fontWeight="medium"
                            sx={{
                              fontSize: "0.95rem",
                              mb: 1,
                            }}
                          >
                            {section.title}
                          </Typography>
                        )}
                        <Stack spacing={2}>
                          {section.fields.map((field) => (
                            <Box key={field.key}>{renderField(field)}</Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  {filterSections.map((section, index) => (
                    <Stack key={section.title || index} spacing={1}>
                      {section.title && (
                        <Typography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{
                            fontSize: "0.95rem",
                            mt: index > 0 ? 1 : 0,
                            mb: 1,
                          }}
                        >
                          {section.title}
                        </Typography>
                      )}
                      <Stack spacing={2}>
                        {section.fields.map((field) => (
                          <Box key={field.key}>{renderField(field)}</Box>
                        ))}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          {/* Footer actions - pinned to bottom */}
          <Stack
            direction="column"
            sx={{
              px: 3,
              py: 2,
              gap: 2,
              backgroundColor: theme.palette.background.paper,
              flexShrink: 0,
              // On mobile, ensure footer stays at bottom and doesn't interfere with scrolling
              ...(isMobile && {
                position: "sticky",
                bottom: 0,
                zIndex: 1,
              }),
            }}
          >
            {/* Preset name field - shown when checkbox is checked */}
            {saveAsPreset && (
              <TextField
                fullWidth
                label="Preset Name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                placeholder="Enter preset name"
                autoFocus
                autoComplete="off"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                }}
                helperText={
                  presetName.trim() &&
                  savedFilters.some((f) => f.name === presetName.trim())
                    ? "A preset with this name already exists. Saving will overwrite it."
                    : undefined
                }
              />
            )}

            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems="center"
              justifyContent="space-between"
              spacing={isMobile ? 2 : 0}
            >
              {isMobile ? (
                // Mobile: Stack buttons vertically with Apply on top
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApply}
                    fullWidth
                    size="large"
                    disabled={isApplyDisabled}
                  >
                    Apply Filters
                  </Button>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Tooltip
                      title="Saved filters are stored in your browser. Clearing browser data or using a different device/browser will remove them."
                      placement="top"
                      arrow
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={saveAsPreset}
                            onChange={(e) =>
                              handleSaveAsPresetChange(e.target.checked)
                            }
                            size="small"
                            color="primary"
                          />
                        }
                        label="Save as preset"
                      />
                    </Tooltip>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={handleCloseAndResetPreset}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </>
              ) : (
                // Desktop: Original layout
                <>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleCloseAndResetPreset}
                  >
                    Cancel
                  </Button>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Tooltip
                      title="Saved filters are stored in your browser. Clearing browser data or using a different device/browser will remove them."
                      placement="top"
                      arrow
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={saveAsPreset}
                            onChange={(e) =>
                              handleSaveAsPresetChange(e.target.checked)
                            }
                            size="small"
                            color="primary"
                          />
                        }
                        label="Save as preset"
                      />
                    </Tooltip>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleApply}
                      fullWidth={isMobile}
                      disabled={isApplyDisabled}
                    >
                      Apply
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

function HorizontalShortcuts(
  props: PickersShortcutsProps<DateRange<DateTime>>
) {
  const { items, onChange, isValid, changeImportance = "accept" } = props;

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const newValue = item.getValue({ isValid });

    return {
      label: item.label,
      onClick: () => {
        onChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box
      sx={{
        width: "100%",
        gridRow: 1,
        gridColumn: 2,
      }}
    >
      <List
        dense
        sx={(theme) => ({
          display: "flex",
          justifyContent: "center",
          px: theme.spacing(2),
          "& .MuiListItem-root": {
            width: "inherit",
            py: 0,
            px: theme.spacing(0.5),
          },
        })}
      >
        {resolvedItems.map((item) => {
          return (
            <ListItem key={item.label}>
              <Chip {...item} />
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
}

function buildTagQueryConfig({
  tagType,
  tenantId,
}: {
  tagType?: TagType;
  tenantId?: string;
}) {
  // Workflow tags don't require tenantId - they're staff-only internal tags
  if (tagType === "workflow") {
    return {
      additionalSearch: "type:Workflow",
    };
  }

  // All other tag types require tenantId for security
  if (!tenantId) {
    return undefined;
  }

  if (!tagType) {
    return { tenantId };
  }

  if (tagType === "facility") {
    return {
      tenantId,
      additionalSearch: "type:Facility",
    };
  }
  if (tagType === "task") {
    return {
      tenantId,
      additionalSearch: "type:Task",
    };
  }
  if (tagType === "waste") {
    return {
      tenantId,
      additionalSearch: "type:Waste",
    };
  }
  return undefined;
}
